/**
 * Coverage request line items and the pricing snapshot: the wire shape the
 * cart submits to submit_coverage_request and portal_coverage_requests stores
 * in its items and pricing_snapshot columns.
 *
 * Type aliases rather than interfaces: only aliases get the implicit index
 * signature that Supabase's Json payload type asks for.
 */

/** Credit detail for an owned subcategory in an upgrade purchase. */
export type UpgradeDetail = {
  readonly subcategorySlug: string;
  readonly displayName: string;
  readonly creditAmount: number;
};

/** Shared fields across all order line item types. */
type OrderLineItemBase = {
  readonly slug: string;
  readonly name: string;
  readonly skuCount: number;
  readonly effectivePrice: number;
};

/** A category subscription line item in the order. */
export type CategoryOrderLineItem = OrderLineItemBase & {
  readonly type: 'category';
  readonly listPrice: number;
  readonly upgradePrice: number;
  readonly upgradeDetails: readonly UpgradeDetail[];
};

/** Brand complement SKUs line item in the order. */
export type BrandComplementOrderLineItem = OrderLineItemBase & {
  readonly type: 'brand-complement';
  readonly brandSlug: string;
  readonly eans: readonly string[];
  readonly unitPrice: number;
};

/** Individual SKUs line item in the order. */
export type IndividualSkuOrderLineItem = OrderLineItemBase & {
  readonly type: 'individual-sku';
  readonly eans: readonly string[];
  readonly categorySource: string;
  readonly unitPrice: number;
  // wire key: serialized into portal_coverage_requests.items -- do not rename
  readonly pseudoBrandId?: string;
};

/** An order line item: one of the three access types. */
export type OrderLineItem =
  | CategoryOrderLineItem
  | BrandComplementOrderLineItem
  | IndividualSkuOrderLineItem;

/** Pricing snapshot captured at request submission time. */
export type PricingSnapshot = {
  readonly existingSpend: number;
  readonly currentDiscountPct: number;
  readonly categorySubtotal: number;
  readonly brandComplementSubtotal: number;
  readonly individualSkuSubtotal: number;
  readonly cartSubtotal: number;
  readonly projectedDiscountPct: number;
  readonly projectedTotal: number;
  readonly netAdditionalCost: number;
};

// ===== Parsing stored rows =====

type Raw = Readonly<Record<string, unknown>>;

function asRecord(value: unknown, what: string): Raw {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${what} is not an object`);
  }
  return value as Raw;
}

function str(raw: Raw, key: string, what: string): string {
  const value = raw[key];
  if (typeof value !== 'string') throw new Error(`${what}: ${key} is not a string`);
  return value;
}

function optionalStr(raw: Raw, key: string, what: string): string | undefined {
  const value = raw[key];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') throw new Error(`${what}: ${key} is not a string`);
  return value;
}

function num(raw: Raw, key: string, what: string): number {
  const value = raw[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${what}: ${key} is not a number`);
  }
  return value;
}

function strings(raw: Raw, key: string, what: string): readonly string[] {
  const value = raw[key];
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) {
    throw new Error(`${what}: ${key} is not a string array`);
  }
  return value;
}

function parseUpgradeDetails(raw: Raw, what: string): readonly UpgradeDetail[] {
  const value = raw['upgradeDetails'];
  if (!Array.isArray(value)) throw new Error(`${what}: upgradeDetails is not an array`);
  return value.map((entry, index) => {
    const detailWhat = `${what} upgradeDetails[${index}]`;
    const detail = asRecord(entry, detailWhat);
    return {
      subcategorySlug: str(detail, 'subcategorySlug', detailWhat),
      displayName: str(detail, 'displayName', detailWhat),
      creditAmount: num(detail, 'creditAmount', detailWhat),
    };
  });
}

/**
 * Parses one stored line item, throwing with the item's context and the
 * offending field so a malformed row fails the read instead of reaching the
 * views as if it were typed. Keys outside the wire shape are dropped.
 */
export function parseOrderLineItem(value: unknown, what: string): OrderLineItem {
  const raw = asRecord(value, what);
  const base = {
    slug: str(raw, 'slug', what),
    name: str(raw, 'name', what),
    skuCount: num(raw, 'skuCount', what),
    effectivePrice: num(raw, 'effectivePrice', what),
  };
  switch (raw['type']) {
    case 'category':
      return {
        ...base,
        type: 'category',
        listPrice: num(raw, 'listPrice', what),
        upgradePrice: num(raw, 'upgradePrice', what),
        upgradeDetails: parseUpgradeDetails(raw, what),
      };
    case 'brand-complement':
      return {
        ...base,
        type: 'brand-complement',
        brandSlug: str(raw, 'brandSlug', what),
        eans: strings(raw, 'eans', what),
        unitPrice: num(raw, 'unitPrice', what),
      };
    case 'individual-sku': {
      const pseudoBrandId = optionalStr(raw, 'pseudoBrandId', what);
      return {
        ...base,
        type: 'individual-sku',
        eans: strings(raw, 'eans', what),
        categorySource: str(raw, 'categorySource', what),
        unitPrice: num(raw, 'unitPrice', what),
        ...(pseudoBrandId === undefined ? {} : { pseudoBrandId }),
      };
    }
    default:
      throw new Error(`${what}: unknown type ${JSON.stringify(raw['type'])}`);
  }
}

/** Parses a stored pricing snapshot; null when the request was stored without one. */
export function parsePricingSnapshot(value: unknown, what: string): PricingSnapshot | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value, what);
  return {
    existingSpend: num(raw, 'existingSpend', what),
    currentDiscountPct: num(raw, 'currentDiscountPct', what),
    categorySubtotal: num(raw, 'categorySubtotal', what),
    brandComplementSubtotal: num(raw, 'brandComplementSubtotal', what),
    individualSkuSubtotal: num(raw, 'individualSkuSubtotal', what),
    cartSubtotal: num(raw, 'cartSubtotal', what),
    projectedDiscountPct: num(raw, 'projectedDiscountPct', what),
    projectedTotal: num(raw, 'projectedTotal', what),
    netAdditionalCost: num(raw, 'netAdditionalCost', what),
  };
}
